import { CountryCode } from '@qred/shared-enums';
import { z } from 'zod';
import { CompanyTypeCategory } from '~/interfaces/shared';
import { ETokenAuthentication } from './shared';

const cardApplicationGetClientsResDTOSchema = z.strictObject({
  clients: z.array(
    z.strictObject({
      active: z.boolean(),
      application_uuid: z.string().optional().nullable(),
      client_id: z.number(),
      company_name: z.string().nullable(),
      company_no: z.string().nullable(),
      company_type: z.string().nullable(),
      email: z.string().nullable(),
      has_inprogress_application: z.boolean().optional(),
      has_offer_to_sign: z.boolean().optional(),
      phone: z.string().nullable(),
      personal_id: z.string().nullable().optional(),
      company_type_category: z.nativeEnum(CompanyTypeCategory).optional(),
      city: z.string().nullable().optional(),
      street: z.string().nullable().optional(),
      zip_code: z.string().nullable().optional(),
    })
  ),
});

const _cardDataPointsSharedSchema = z.strictObject({
  company: z.boolean(),
  cardPurpose: z.boolean(),
  cardPurposeReason: z.boolean(),
  turnover: z.boolean(),
  applicantEmail: z.boolean(),
  applicantPhone: z.boolean(),
  applicantPersonalNumber: z.boolean(),
  applicantDateOfBirth: z.boolean(),
  applicantFullName: z.boolean(),
  applicantAddress: z.boolean(),
});

export type CardDataPointsShared = z.infer<typeof _cardDataPointsSharedSchema>;

const cardApplicationGetCompanySearchResDTOCompanySchema = z.strictObject({
  companyName: z.string(),
  companyNumber: z.string().optional(),
  companyType: z.string().optional(),
  address: z
    .object({
      city: z.string(),
      postcode: z.string(),
      street: z.string(),
    })
    .optional(),
});

const cardApplicationGetCompanySearchResDTOSchema = z.array(
  cardApplicationGetCompanySearchResDTOCompanySchema
);

export const cardApplicationDTOSchemas = {
  getClientsRes: cardApplicationGetClientsResDTOSchema,
  getCompanySearchRes: cardApplicationGetCompanySearchResDTOSchema,
};

const addressFieldsSchema = z.strictObject({
  city: z.string().min(1).optional(),
  houseNumber: z.string().min(1).optional(),
  houseNumberAddition: z.string().optional(),
  streetName: z.string().min(1).optional(),
  zipCode: z.string().min(1).optional(),
});

export const cardApplicationDTOSchema = z.object({
  applicant: z
    .object({
      firstName: z.string().optional(),
      lastName: z.string().optional(),
      address: z
        .object({
          city: z.string(),
          street: z.string(), // streetName + houseNumber + houseNumberAddition
          zipCode: z.string(),
        })
        .optional(),
      addressFields: addressFieldsSchema.optional(),
      dateOfBirth: z.string().optional(),
      email: z.string().optional(),
      personalNumber: z.string().optional(),
      phone: z.string().optional(),
    })
    .optional(),
  applicantAuthenticated: z.boolean(),
  applicantIdentified: z.boolean(),
  applicationUuid: z.string().optional(),
  clientId: z.number().optional(),
  completedStep: z.number(),
  currentStep: z.number().optional(),
  finalized: z.boolean(),
  landingUrl: z.string().optional(),
  market: z.nativeEnum(CountryCode),
  organization: z.object({
    organizationNumber: z.string(),
    companyType: z.string().optional(),
    companyName: z.string().optional(),
    vatNumber: z.string().optional(),
    companyAddress: z
      .object({
        city: z.string().min(1),
        zipCode: z.string().min(1),
        street: z.string().min(1),
      })
      .optional(),
  }),
  cardPurposeManual: z.string().optional(),
  purposeOfCard: z.string().optional(),
  referenceUrl: z.string().optional(),
  sessionId: z.string(),
  trafficCookie: z.string().optional(),
  qCode: z.string().optional(),
  applicationId: z.number().optional(),
  userCode: z.string().optional(),
  dataPointsShared: _cardDataPointsSharedSchema,
  createdAt: z.string().optional(),
  accessToken: z.string().optional(),
  tokenAuthentication: z.nativeEnum(ETokenAuthentication).optional(),
  shouldPostApplication: z.boolean().optional(), // Not in place in the backend yet
  currentStepCompleted: z.boolean().optional(), // Not in place in the backend yet
  turnover: z.string(),
});

export type CardApplicationDTO = z.infer<typeof cardApplicationDTOSchema>;

export const cardApplicationPostPatchResponseSchema = z.object({
  applicationUuid: z.string(),
  clientId: z.number(),
  applicationId: z.number(),
  qCode: z.string().optional(),
  userCode: z.string().optional(),
  accessToken: z.string().optional(),
});

export type CardApplicationPostPatchResponseDTO = z.infer<
  typeof cardApplicationPostPatchResponseSchema
>;
